<template>
  <div class="message-queue-message-table" v-loading="loading">
    <!-- button group-->
    <div class="mb-x2" layout="layout" layout-align="space-between-end">
      <el-button class="el-button-mt" type="default" @click="changeTab('queue-list')"
        ><i class="mdi mdi-keyboard-backspace"></i>一覧に戻る</el-button
      >
      <el-select class="mini-select-box" v-model="status.value" placeholder="配信結果" @change="status.onChange">
        <el-option v-for="item in status.options" :key="item.value" :label="item.label" :value="item.value"></el-option>
      </el-select>
      <csv-download :disabled="!queueMessages.length || isDownloading" :download="downloadCsv"></csv-download>
    </div>
    <!-- table-->
    <el-table class="mb-x2" :data="queueMessages" stripe @header-click="headerClick">
      <el-table-column label="配信開始日時" sort-by="-actionDatetime" :render-header="renderHeader" min-width="30%">
        <template slot-scope="scope">{{ formatDatetime(scope.row.actionDatetime) }}</template>
      </el-table-column>
      <el-table-column label="配信結果" sort-by="-status" :render-header="renderHeader" min-width="30%">
        <template slot-scope="scope">
          <div class="text-info" v-if="scope.row.status === 'unsent'">配信待ち</div>
          <div class="text-success" v-if="scope.row.status === 'sent'">配信成功</div>
          <div class="text-danger" v-if="scope.row.status === 'failed'">配信失敗</div>
          <div class="text-disabled" v-if="scope.row.errorType === 'unfollow'">{{ '(フォローされていない)' }}</div>
          <div class="text-disabled" v-if="scope.row.errorType === 'user_not_exists'">
            {{ '(' }}{{ 'ユーザーが存在しない)' }}
          </div>
          <div class="text-disabled" v-if="scope.row.errorType === 'not_found_serial_code'">
            {{ '(シリアルコード不足)' }}
          </div>
        </template>
      </el-table-column>
      <el-table-column label="宛先アカウント" sort-by="name" :render-header="renderHeader" min-width="40%">
        <template slot-scope="scope">
          <applicant-name :applicant="scope.row.applicant"></applicant-name>
        </template>
      </el-table-column>
    </el-table>
    <pagination
      :total="total"
      :pagingNo="params.pagingNo"
      :limitCount="params.limitCount"
      :onChangePagingNo="onChangePagingNo"
      :onChangeLimitCount="onChangeLimitCount"
    ></pagination>
  </div>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.message-queue-message-table {
  :deep(.el-input__inner) {
    height: $spacing-unit * 4 + 1;
  }

  :deep(.text-disabled) {
    font-size: $font-small-size;
  }
}
</style>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import moment from 'moment'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import ApplicantName from '@/client/components/_atoms/applicant_name'
import CsvDownload from '@/client/components/_atoms/csv_download'
import Pagination from '@/client/components/_atoms/pagination'
import api from '@/client/core/api'
import Util from '@/client/core/util'
import { formatDatetime } from '@/client/filters/filters'
import { useTwitterInstantwinQueueStore } from '@/client/store/useTwitterInstantwinQueueStore'

export default defineComponent({
  data() {
    return {
      isDownloading: false,
      status: {
        value: null,
        options: [
          {
            label: '指定なし',
            value: ''
          },
          {
            label: '配信待ち',
            value: 'unsent'
          },
          {
            label: '配信成功',
            value: 'sent'
          },
          {
            label: '配信失敗',
            value: 'failed'
          }
        ],
        onChange: (value: string) => {
          if (this.params.status === value) return

          this.setQueueMessageParams({ status: value, pagingNo: 1 })
        }
      }
    }
  },
  components: {
    ApplicantName,
    CsvDownload,
    Pagination
  },
  computed: {
    ...mapState(useTwitterInstantwinQueueStore, {
      queueMessages: state => state.queueMessages.list,
      total: state => state.queueMessages.total,
      params: state => state.queueMessages.params,
      queueId: state => state.queueMessages.queueId,
      loading: state => state.loading
    })
  },
  async mounted() {
    await this.fetchQueueMessage()
  },
  watch: {
    params() {
      this.fetchQueueMessage()
    }
  },
  methods: {
    formatDatetime,

    ...mapActions(useTwitterInstantwinQueueStore, ['fetchQueueMessage', 'setQueueMessageParams']),

    /**
     * CSVダウンロード
     */
    async downloadCsv() {
      try {
        if (this.isDownloading) {
          return
        }

        const queueMessages = await this.getDataForCsv()
        const filename = `instantwin_sent_messages_${moment().format('YYYY_MM_DD')}`
        const csvData = []

        csvData.push([
          '配信開始日時',
          '配信結果',
          'アカウント名',
          'アカウントスクリーンネーム',
          'アカウント画像',
          'フォロー中',
          '警告アカウント',
          'NGアカウント',
          '新規獲得フォロワー'
        ])

        queueMessages.forEach(message => {
          let status = ''
          switch (message.status) {
            case 'sent':
              status = '配信成功'
              break
            case 'failed':
              status = '配信失敗'
              break
            case 'unsent':
              status = '配信待ち'
              break
          }

          csvData.push([
            formatDatetime(message.actionDatetime),
            status,
            message.applicant.name,
            message.applicant.screenName,
            message.applicant.pictureUrl,
            message.applicant.isFollowing ? 1 : 0,
            message.applicant.isWarning ? 1 : 0,
            message.applicant.isNg ? 1 : 0,
            message.applicant.isNewFollower && message.applicant.isFollowing ? 1 : 0
          ])
        })

        return {
          filename,
          data: csvData
        }
      } catch {
        this.$notify({
          title: 'CSVダウンロードに失敗しました。',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        this.isDownloading = false
      }
    },

    /**
     * Get queue message
     */
    async getDataForCsv() {
      this.isDownloading = true

      const params = Object.assign({}, this.params, {
        pagingNo: 1,
        limitCount: 1000
      })

      let totalResults = []

      const LOOP = true

      while (LOOP) {
        const result = await api.get(`/twitter_instantwin_queues/${this.queueId}/messages`, { params })

        if (result.error) {
          throw new Error('Download error!')
        }

        if (!result.data || !result.data.messages.length) {
          break
        }

        totalResults = totalResults.concat(result.data.messages)
        params.pagingNo = params.pagingNo + 1
      }

      setTimeout(() => {
        this.isDownloading = false
      }, 2000)

      return totalResults
    },

    /**
     * Change screen
     * @param {string} tab
     */
    changeTab(tab: string) {
      this.$emit('change_tab', tab)
    },

    /**
     * ページを変更イベント
     * @param {number} pagingNo ページング
     */
    onChangePagingNo(pagingNo: number) {
      this.setQueueMessageParams({
        pagingNo
      })

      Util.scrollToTop()
    },

    /**
     * ページを変更イベント
     * @param {number} limitCount リミット数
     */
    onChangeLimitCount(limitCount: number) {
      this.setQueueMessageParams({
        limitCount
      })
    },

    /**
     * ソートを変更イベント
     * @param {string} sortBy ソート
     */
    onChangeSortBy(sortBy: string) {
      if (this.params.sortBy === sortBy) {
        return
      }

      this.setQueueMessageParams({ sortBy })
    },

    /**
     * ヘッダーをクリックするとソートする
     * @param {*} column
     */
    headerClick(column: any) {
      if (!column.sortBy) {
        return
      }
      if (column.sortBy.indexOf('-') === 0) {
        if (this.params.sortBy === column.sortBy) {
          this.onChangeSortBy(column.sortBy.substring(1))
        } else {
          this.onChangeSortBy(column.sortBy)
        }
      } else {
        if (this.params.sortBy === column.sortBy) {
          this.onChangeSortBy('-' + column.sortBy)
        } else {
          this.onChangeSortBy(column.sortBy)
        }
      }
    },

    /**
     * ヘッダー描画
     * @param {*} createElement
     * @param {*} { column }
     */
    renderHeader(createElement: any, { column }: any) {
      return Util.renderHeader(
        createElement,
        column.label,
        Util.getSortClass(this.params.sortBy, column.sortBy),
        !!column.sortBy
      )
    }
  }
})
</script>
